/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2, Calculator, Download, Sparkles, FileUp, RotateCw, X, Check, AlertCircle,
  FileSpreadsheet, ShieldCheck, FileText, ShieldAlert,
} from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { DriftBanner } from "./additions/ExcelRoundTripModal";

const inr = (v) => {
  const n = Number(v || 0);
  if (!n) return "–";
  const s = Math.abs(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `(${s})` : s;
};

const SOURCE_META = {
  manual:       { label: "Manual",      cls: "bg-slate-100 text-slate-700 border-slate-200" },
  manual_xlsx:  { label: "Excel",       cls: "bg-sky-50 text-sky-800 border-sky-200" },
  prior_3cd:    { label: "Prior 3CD",   cls: "bg-amber-50 text-amber-800 border-amber-200" },
  prior_run:    { label: "Rolled fwd",  cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
};

export default function ComputeTab({ rid }) {
  const [openings, setOpenings] = useState([]);
  const [busy, setBusy] = useState(false);
  const [computing, setComputing] = useState(false);
  const [result, setResult] = useState(null);

  // Phase 1D — Prior 3CD Import
  const priorFileRef = useRef(null);
  const [priorUploading, setPriorUploading] = useState(false);
  const [priorModal, setPriorModal] = useState(null); // {filename, rows: [{rate, closing_wdv, suggested_block_label, candidate_block_labels, needs_review, desc_block_assets}]}

  // Phase 1H — Roll forward preview
  const [rollSource, setRollSource] = useState(null); // {ok, src_fy, src_name, src_run_id, items}
  const [rollApplying, setRollApplying] = useState(false);
  const [rollModalOpen, setRollModalOpen] = useState(false);

  // Excel round-trip for Opening WDV (per-block sub-allocation)
  const xlsxFileRef = useRef(null);
  const [xlsxExporting, setXlsxExporting] = useState(false);
  const [xlsxImporting, setXlsxImporting] = useState(false);

  // Optional 3CD validation
  const validateFileRef = useRef(null);
  const [validating, setValidating] = useState(false);
  const [validateModal, setValidateModal] = useState(null); // {ok, rows, totals, filename}

  // Drift banner (carried over from Excel re-import on Additions tab)
  const [driftWarning, setDriftWarning] = useState(null);
  const [clearingDrift, setClearingDrift] = useState(false);

  // Prior-3CD validation gate
  const [validationGate, setValidationGate] = useState(null);  // {ok, mismatch_count, totals, validated_at, filename, acknowledged}
  const [overriding, setOverriding] = useState(false);

  const refreshRun = useCallback(async () => {
    if (!rid) return;
    try {
      const { data } = await http.get(`/fixed-assets/runs/${rid}`);
      setDriftWarning(data?.excel_drift_warning || null);
      setValidationGate(data?.prior_3cd_validation || null);
    } catch { /* swallow */ }
  }, [rid]);

  const clearDrift = async () => {
    setClearingDrift(true);
    try {
      await http.post(`/fixed-assets/runs/${rid}/clear-excel-drift`);
      setDriftWarning(null);
      toast.success("Drift warning cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear");
    } finally { setClearingDrift(false); }
  };

  const refreshOpening = useCallback(async () => {
    if (!rid) return;
    setBusy(true);
    try {
      const { data } = await http.get(`/fixed-assets/runs/${rid}/block-opening`);
      setOpenings(data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load opening WDV");
    } finally { setBusy(false); }
  }, [rid]);

  const refreshRollSource = useCallback(async () => {
    if (!rid) return;
    try {
      const { data } = await http.get(`/fixed-assets/runs/${rid}/roll-forward-source`);
      setRollSource(data || null);
    } catch {
      setRollSource(null);
    }
  }, [rid]);

  useEffect(() => { refreshOpening(); refreshRollSource(); refreshRun(); }, [refreshOpening, refreshRollSource, refreshRun]);

  const saveOpening = async (block_label, opening_wdv, description) => {
    setOpenings(rs => rs.map(r => r.block_label === block_label
      ? { ...r, opening_wdv: parseFloat(opening_wdv || 0), description: description ?? r.description, source: "manual" }
      : r));
    try {
      await http.post(`/fixed-assets/runs/${rid}/block-opening`, {
        block_label,
        opening_wdv: parseFloat(opening_wdv || 0),
        description: description || "",
      });
      refreshRun();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      refreshOpening();
    }
  };

  // ---------- Prior 3CD upload ----------
  const on3CDPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = "";
    setPriorUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await http.post(`/fixed-assets/runs/${rid}/ingest-prior-3cd`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPriorModal({ filename: f.name, rows: data?.rows || [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not parse 3CD JSON");
    } finally { setPriorUploading(false); }
  };

  const apply3CD = async (items) => {
    const payload = {
      items: items
        .filter(r => r.block_label && Number(r.opening_wdv) >= 0)
        .map(r => ({ rate: Number(r.rate || 0), block_label: r.block_label, opening_wdv: Number(r.opening_wdv || 0) })),
    };
    if (!payload.items.length) {
      toast.error("Pick a block for at least one row before applying.");
      return;
    }
    try {
      const { data } = await http.post(`/fixed-assets/runs/${rid}/apply-prior-3cd`, payload);
      toast.success(`Opening WDV applied for ${data.applied} block${data.applied === 1 ? "" : "s"}`);
      setPriorModal(null);
      refreshOpening();
      refreshRun();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Apply failed");
    }
  };

  // ---------- Roll forward ----------
  const applyRollForward = async () => {
    setRollApplying(true);
    try {
      const { data } = await http.post(`/fixed-assets/runs/${rid}/roll-forward`);
      toast.success(`Rolled forward ${data.applied} block${data.applied === 1 ? "" : "s"} from FY ${data.src_fy}`);
      setRollModalOpen(false);
      refreshOpening();
      refreshRun();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Roll-forward failed");
    } finally { setRollApplying(false); }
  };

  // ---------- Excel round-trip for Opening WDV ----------
  const exportOpeningXlsx = async () => {
    setXlsxExporting(true);
    try {
      const res = await http.get(`/fixed-assets/runs/${rid}/block-opening/export.xlsx`,
                                 { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      const cd = res.headers["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/i.exec(cd);
      a.href = url;
      a.download = m?.[1] || "OpeningWDV.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally { setXlsxExporting(false); }
  };

  const onOpeningXlsxPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = "";
    setXlsxImporting(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await http.post(
        `/fixed-assets/runs/${rid}/block-opening/import.xlsx`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const unk = (data.unknown_blocks || []).length;
      toast.success(
        `Updated ${data.applied} block${data.applied === 1 ? "" : "s"}`
        + (unk ? ` · ${unk} unknown row${unk === 1 ? "" : "s"} skipped` : ""),
      );
      refreshOpening();
      refreshRun();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setXlsxImporting(false); }
  };

  // ---------- Optional 3CD validation ----------
  const onValidate3CDPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = "";
    setValidating(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await http.post(
        `/fixed-assets/runs/${rid}/block-opening/validate-3cd`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setValidateModal({ ...data, filename: data.filename || f.name });
      // The server persists a compact summary on the run — refresh the
      // gate state so the Compute button enables/disables accordingly.
      refreshRun();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Validation failed");
    } finally { setValidating(false); }
  };

  const overrideValidation = async () => {
    setOverriding(true);
    try {
      await http.post(`/fixed-assets/runs/${rid}/clear-3cd-validation-warning`);
      toast.success("Override applied — Compute re-enabled");
      refreshRun();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Override failed");
    } finally { setOverriding(false); }
  };

  // ---------- PDF working-paper ----------
  // Excel + PDF downloads moved to the Summary tab.

  const compute = async () => {
    setComputing(true); setResult(null);
    try {
      const { data } = await http.post(`/fixed-assets/runs/${rid}/compute`);
      setResult(data);
      toast.success(`Depreciation ₹ ${inr(data.totals.depreciation)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Compute failed");
    } finally { setComputing(false); }
  };

  const totalOpening = useMemo(() => openings.reduce((s, r) => s + Number(r.opening_wdv || 0), 0), [openings]);
  const rollAvailable = rollSource?.ok && (rollSource.items || []).length > 0;
  // Compute is blocked while a prior-3CD validation flagged a mismatch
  // and the auditor hasn't explicitly overridden it.
  const computeBlocked = !!(validationGate && !validationGate.ok && !validationGate.acknowledged);

  return (
    <div className="space-y-5">
      <DriftBanner warning={driftWarning} onClear={clearDrift} clearing={clearingDrift}/>

      {/* Opening WDV import toolbar */}
      <div className="bg-[#FAFAF7] border border-[#E5E5E0] p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[220px] text-[12px] text-[#52524E]">
            <span className="font-semibold text-slate-800">Opening WDV</span>
            <span className="ml-2 text-slate-500">
              Edit inline below, or use the Excel round-trip to fill values for every sub-block at the same rate.
            </span>
          </div>
          <input
            ref={xlsxFileRef}
            type="file"
            accept=".xlsx"
            onChange={onOpeningXlsxPick}
            className="hidden"
            data-testid="fa-opening-xlsx-input"
          />
          <button
            data-testid="fa-opening-export-xlsx"
            onClick={exportOpeningXlsx}
            disabled={xlsxExporting}
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-slate-300 hover:bg-slate-100 text-slate-800 text-[12.5px] disabled:opacity-60"
          >
            {xlsxExporting ? <Loader2 size={13} className="animate-spin"/> : <Download size={13}/>}
            Export Opening WDV (Excel)
          </button>
          <button
            data-testid="fa-opening-import-xlsx"
            onClick={() => xlsxFileRef.current?.click()}
            disabled={xlsxImporting}
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-sky-300 bg-sky-50 hover:bg-sky-100 text-sky-900 text-[12.5px] disabled:opacity-60"
          >
            {xlsxImporting ? <Loader2 size={13} className="animate-spin"/> : <FileSpreadsheet size={13}/>}
            Import Opening WDV (Excel)
          </button>
          <button
            data-testid="fa-roll-forward-btn"
            onClick={() => rollAvailable && setRollModalOpen(true)}
            disabled={!rollAvailable}
            title={rollAvailable
              ? `Roll forward closing WDV from FY ${rollSource.src_fy}`
              : "No prior FY run found for this client"}
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 text-[12.5px] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <RotateCw size={13}/>
            {rollAvailable ? `Roll forward from FY ${rollSource.src_fy}` : "Roll forward (no prior FY)"}
          </button>
        </div>

        {/* Optional 3CD row — separated so it doesn't read as the primary path */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-dashed border-[#E5E5E0]">
          <div className="flex-1 min-w-[220px] text-[11.5px] text-[#6B6B66]">
            <span className="font-mono uppercase tracking-wider text-[10px] mr-2 text-slate-500">Optional</span>
            Prior-year 3CD JSON — only carries rate-level totals. Use it to validate sub-block sums or seed a single-block-per-rate.
          </div>
          <input
            ref={validateFileRef}
            type="file"
            accept=".json,application/json"
            onChange={onValidate3CDPick}
            className="hidden"
            data-testid="fa-3cd-validate-input"
          />
          <button
            data-testid="fa-3cd-validate-btn"
            onClick={() => validateFileRef.current?.click()}
            disabled={validating}
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-violet-300 bg-violet-50 hover:bg-violet-100 text-violet-900 text-[12px] disabled:opacity-60"
          >
            {validating ? <Loader2 size={13} className="animate-spin"/> : <ShieldCheck size={13}/>}
            Validate against Prior 3CD
          </button>
          <input
            ref={priorFileRef}
            type="file"
            accept=".json,application/json"
            onChange={on3CDPick}
            className="hidden"
            data-testid="fa-3cd-file-input"
          />
          <button
            data-testid="fa-3cd-import-btn"
            onClick={() => priorFileRef.current?.click()}
            disabled={priorUploading}
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-200 bg-white hover:bg-amber-50 text-amber-900 text-[12px] disabled:opacity-60"
            title="Import 3CD opening WDV by rate (single block per rate). For multi-block rates use the Excel round-trip above."
          >
            {priorUploading ? <Loader2 size={13} className="animate-spin"/> : <FileUp size={13}/>}
            Import from Prior 3CD
          </button>
        </div>
      </div>

      {/* Opening WDV table */}
      <div className="bg-white border border-[#E5E5E0]">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#EDEDE7]">
          <div className="flex items-center gap-2">
            <Sparkles size={15} className="text-amber-600"/>
            <h2 className="font-heading text-base">Opening WDV by Block</h2>
            <span className="font-mono text-[11px] text-slate-500">Total ₹ {inr(totalOpening)}</span>
          </div>
          {busy && <Loader2 size={13} className="animate-spin text-slate-500"/>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left bg-[#F9F9F8] text-[10.5px] font-mono uppercase tracking-wider text-slate-600">
                <th className="px-4 py-2">Block</th>
                <th className="px-3 py-2 text-center">Rate</th>
                <th className="px-3 py-2 text-right w-[180px]">Opening WDV (₹)</th>
                <th className="px-3 py-2 w-[120px]">Source</th>
                <th className="px-3 py-2">Note (optional)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EDEDE7]">
              {openings.map(b => <OpeningRow key={b.block_label} row={b} onSave={saveOpening}/>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* 3CD validation gate (drift-banner-style) */}
      <Validation3CDBanner
        gate={validationGate}
        onOverride={overrideValidation}
        overriding={overriding}
      />

      {/* Compute */}
      <div className="bg-white border border-[#E5E5E0] p-4 flex items-center justify-between gap-3">
        <div>
          <div className="font-heading text-base">Run Computation</div>
          <p className="text-[12px] text-[#52524E] mt-0.5 max-w-3xl">
            Aggregates Opening WDV + every confirmed addition (with adjustment columns) − every credit marked
            as Sale, applies the 180-day half-rate rule, and produces the IT Depreciation Schedule.
            Excel + PDF deliverables are downloadable from the <strong>Summary</strong> tab.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="fa-compute-btn"
            onClick={compute}
            disabled={computing || computeBlocked}
            title={computeBlocked
              ? "3CD validation found mismatches — review the banner above or click 'Proceed anyway'."
              : "Run the computation"}
            className="inline-flex items-center gap-2 px-3.5 py-2 bg-slate-900 text-white text-[13px] hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {computing ? <Loader2 size={14} className="animate-spin"/> : <Calculator size={14}/>}
            Compute
          </button>
        </div>
      </div>

      {/* Result table */}
      {result && (
        <div className="bg-white border border-[#E5E5E0]">
          <div className="px-4 py-3 border-b border-[#EDEDE7]">
            <h2 className="font-heading text-base">Depreciation Schedule</h2>
            <div className="text-[11.5px] text-slate-500 mt-0.5">
              {result.rows.length} blocks active · Depreciation ₹ {inr(result.totals.depreciation)} · Closing WDV ₹ {inr(result.totals.closing_wdv)}
              {result.totals.stcg_sec50 > 0 && <> · STCG u/s 50: ₹ {inr(result.totals.stcg_sec50)}</>}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-left bg-[#F9F9F8] text-[10.5px] font-mono uppercase tracking-wider text-slate-600">
                  <th className="px-3 py-2">Block</th>
                  <th className="px-3 py-2 text-center">Rate</th>
                  <th className="px-3 py-2 text-right">Opening WDV</th>
                  <th className="px-3 py-2 text-right">Adds ≥ 180d</th>
                  <th className="px-3 py-2 text-right">Adds &lt; 180d</th>
                  <th className="px-3 py-2 text-right">Sales</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-right">Depn</th>
                  <th className="px-3 py-2 text-right">STCG u/s 50</th>
                  <th className="px-3 py-2 text-right">Closing WDV</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EDEDE7]">
                {result.rows.map(r => (
                  <tr key={r.block_label} className={r.block_extinguished ? "bg-rose-50" : ""}>
                    <td className="px-3 py-1.5 font-medium">{r.block_label}</td>
                    <td className="px-3 py-1.5 text-center font-mono">{r.rate}%</td>
                    <td className="px-3 py-1.5 text-right font-mono">{inr(r.opening_wdv)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{inr(r.adds_full)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{inr(r.adds_half)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{inr(r.deletions)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{inr(r.total_block)}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-semibold">{inr(r.depreciation)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{r.stcg_sec50 ? inr(r.stcg_sec50) : "–"}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-semibold">{inr(r.closing_wdv)}</td>
                  </tr>
                ))}
                <tr className="bg-[#F2F2EE] font-semibold">
                  <td className="px-3 py-2" colSpan={2}>TOTAL</td>
                  <td className="px-3 py-2 text-right font-mono">{inr(result.totals.opening_wdv)}</td>
                  <td className="px-3 py-2 text-right font-mono">{inr(result.totals.adds_full)}</td>
                  <td className="px-3 py-2 text-right font-mono">{inr(result.totals.adds_half)}</td>
                  <td className="px-3 py-2 text-right font-mono">{inr(result.totals.deletions)}</td>
                  <td className="px-3 py-2 text-right font-mono">{inr(result.totals.total_block)}</td>
                  <td className="px-3 py-2 text-right font-mono">{inr(result.totals.depreciation)}</td>
                  <td className="px-3 py-2 text-right font-mono">{result.totals.stcg_sec50 ? inr(result.totals.stcg_sec50) : "–"}</td>
                  <td className="px-3 py-2 text-right font-mono">{inr(result.totals.closing_wdv)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {priorModal && (
        <Prior3CDModal
          filename={priorModal.filename}
          rows={priorModal.rows}
          onClose={() => setPriorModal(null)}
          onApply={apply3CD}
        />
      )}
      {rollModalOpen && rollSource?.ok && (
        <RollForwardModal
          source={rollSource}
          applying={rollApplying}
          onClose={() => setRollModalOpen(false)}
          onApply={applyRollForward}
        />
      )}
      {validateModal && (
        <Validate3CDModal
          report={validateModal}
          onClose={() => setValidateModal(null)}
        />
      )}
    </div>
  );
}

/* ==================== 3CD validation gate banner ==================== */
function Validation3CDBanner({ gate, onOverride, overriding }) {
  if (!gate) return null;
  // Acknowledged or green ⇒ render a quiet emerald confirmation strip.
  if (gate.acknowledged) {
    const label = gate.ok
      ? `3CD validation passed${gate.filename ? ` against ${gate.filename}` : ""}.`
      : `3CD mismatch overridden by auditor${gate.filename ? ` (${gate.filename})` : ""} — Compute re-enabled.`;
    return (
      <div
        data-testid="fa-3cd-gate-banner-ack"
        className="bg-emerald-50 border border-emerald-200 text-emerald-900 px-4 py-2 text-[12px] flex items-center gap-2"
      >
        <Check size={13} className="text-emerald-700 shrink-0"/>
        <span>{label}</span>
        {gate.validated_at && (
          <span className="text-emerald-700/70 font-mono ml-auto text-[10.5px]">
            {gate.validated_at.slice(0, 10)} {gate.validated_at.slice(11, 16)} UTC
          </span>
        )}
      </div>
    );
  }
  // Mismatch and not acknowledged ⇒ rose blocking banner.
  return (
    <div
      data-testid="fa-3cd-gate-banner-block"
      className="bg-rose-50 border border-rose-300 text-rose-900 px-4 py-3 flex flex-wrap items-center gap-3"
    >
      <ShieldAlert size={16} className="text-rose-700 shrink-0"/>
      <div className="text-[12.5px] flex-1 min-w-[260px]">
        <strong>Prior-3CD validation found {gate.mismatch_count} mismatch{gate.mismatch_count === 1 ? "" : "es"}.</strong>{" "}
        Sub-block opening totals don't tie back to {gate.filename || "the uploaded 3CD"}.
        Adjust the Opening WDV and re-validate, or proceed anyway.
      </div>
      <div className="text-[10.5px] font-mono text-rose-700/70 mr-auto">
        Total drift ₹ {Math.abs(Number(gate.totals?.diff || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
      </div>
      <button
        data-testid="fa-3cd-gate-override"
        onClick={onOverride}
        disabled={overriding}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-700 hover:bg-rose-800 text-white text-[12px] disabled:opacity-60"
      >
        {overriding ? <Loader2 size={12} className="animate-spin"/> : <Check size={12}/>}
        I've reviewed — proceed anyway
      </button>
    </div>
  );
}

function OpeningRow({ row, onSave }) {  const [v, setV] = useState(row.opening_wdv || 0);
  const [n, setN] = useState(row.description || "");
  useEffect(() => { setV(row.opening_wdv || 0); setN(row.description || ""); }, [row.block_label, row.opening_wdv, row.description]);
  const meta = SOURCE_META[row.source] || SOURCE_META.manual;
  return (
    <tr className="hover:bg-[#FBFBF8]" data-testid={`fa-opening-${row.block_label}`}>
      <td className="px-4 py-1.5 font-medium">{row.block_label}</td>
      <td className="px-3 py-1.5 text-center font-mono">{row.rate}%</td>
      <td className="px-3 py-1.5">
        <input
          type="number"
          step="0.01"
          value={v}
          onChange={(e) => setV(e.target.value)}
          onBlur={() => onSave(row.block_label, v, n)}
          className="w-full text-right px-2 py-1 border border-[#E5E5E0] focus:border-slate-700 focus:outline-none text-[12.5px] font-mono"
        />
      </td>
      <td className="px-3 py-1.5">
        <span
          className={`inline-block text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border ${meta.cls}`}
          data-testid={`fa-opening-source-${row.block_label}`}
        >
          {meta.label}
        </span>
      </td>
      <td className="px-3 py-1.5">
        <input
          type="text"
          value={n}
          onChange={(e) => setN(e.target.value)}
          onBlur={() => onSave(row.block_label, v, n)}
          placeholder="e.g. carried from FY 2023-24 closing WDV (3CD AY24-25)"
          className="w-full px-2 py-1 border border-[#E5E5E0] focus:border-slate-700 focus:outline-none text-[12px]"
        />
      </td>
    </tr>
  );
}

/* ==================== Prior 3CD import modal ==================== */
function Prior3CDModal({ filename, rows, onClose, onApply }) {
  // local editable draft of each staged row — auditor can override block
  // and fine-tune the opening WDV before committing.
  const [draft, setDraft] = useState(() => rows.map(r => ({
    rate:                    Number(r.rate || 0),
    desc_block_assets:       r.desc_block_assets || "",
    prior_closing_wdv:       Number(r.closing_wdv || 0),
    prior_opening_wdv:       Number(r.opening_wdv || 0),
    opening_wdv:             Number(r.closing_wdv || 0), // prior closing = current opening
    suggested_block_label:   r.suggested_block_label || "",
    candidate_block_labels:  r.candidate_block_labels || [],
    block_label:             r.suggested_block_label || "",
    needs_review:            !!r.needs_review,
  })));

  const total = draft.reduce((s, r) => s + (r.block_label ? Number(r.opening_wdv || 0) : 0), 0);
  const readyCount = draft.filter(r => r.block_label).length;

  const update = (idx, patch) => setDraft(d => d.map((r, i) => i === idx ? { ...r, ...patch } : r));

  return (
    <ModalShell title="Import Opening WDV from Prior Year 3CD" subtitle={filename} onClose={onClose} maxW="max-w-5xl">
      <div className="px-5 py-3 text-[12.5px] text-[#52524E] bg-amber-50 border-b border-amber-200 flex items-start gap-2">
        <AlertCircle size={14} className="text-amber-700 mt-0.5 shrink-0"/>
        <div>
          Each rate row in the uploaded 3CD maps to one or more IT blocks that share that rate. When the mapping
          isn't unique, pick the correct block from the dropdown. <b>Prior year's Closing WDV becomes current year's Opening WDV.</b>
        </div>
      </div>

      <div className="overflow-x-auto max-h-[55vh]">
        <table className="w-full text-[12.5px]">
          <thead className="sticky top-0">
            <tr className="text-left bg-[#F9F9F8] text-[10.5px] font-mono uppercase tracking-wider text-slate-600">
              <th className="px-4 py-2 text-center">Rate</th>
              <th className="px-3 py-2">Description (3CD)</th>
              <th className="px-3 py-2 text-right">Prior Closing WDV</th>
              <th className="px-3 py-2 text-right w-[170px]">→ Opening WDV (₹)</th>
              <th className="px-3 py-2 w-[260px]">Target IT Block</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#EDEDE7]">
            {draft.map((r, i) => (
              <tr
                key={`${r.rate}-${i}`}
                className={r.needs_review && !r.block_label ? "bg-amber-50/40" : ""}
                data-testid={`fa-3cd-staged-row-${i}`}
              >
                <td className="px-4 py-1.5 text-center font-mono font-semibold">{r.rate}%</td>
                <td className="px-3 py-1.5 text-[12px] text-slate-700 max-w-[260px] truncate" title={r.desc_block_assets}>
                  {r.desc_block_assets || <span className="text-slate-400">—</span>}
                </td>
                <td className="px-3 py-1.5 text-right font-mono">{inr(r.prior_closing_wdv)}</td>
                <td className="px-3 py-1.5">
                  {/* 2026-09-09 — same fix as AdditionRow's Forex box: keep
                      the raw typed string (no per-keystroke parseFloat) so
                      the caret doesn't reset on a type="number" re-render;
                      normalise to a real number on blur. */}
                  <input
                    type="text" inputMode="decimal"
                    value={r.opening_wdv === undefined || r.opening_wdv === null ? "" : String(r.opening_wdv)}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || /^-?\d*\.?\d*$/.test(v)) update(i, { opening_wdv: v });
                    }}
                    onBlur={() => {
                      const parsed = parseFloat(r.opening_wdv);
                      update(i, { opening_wdv: Number.isFinite(parsed) ? parsed : 0 });
                    }}
                    className="w-full text-right px-2 py-1 border border-[#E5E5E0] focus:border-slate-700 focus:outline-none text-[12.5px] font-mono"
                    data-testid={`fa-3cd-opening-input-${i}`}
                  />
                </td>
                <td className="px-3 py-1.5">
                  {r.candidate_block_labels.length === 0 ? (
                    <span className="text-[11px] text-rose-600">No block at rate {r.rate}%</span>
                  ) : (
                    <select
                      value={r.block_label}
                      onChange={(e) => update(i, { block_label: e.target.value })}
                      className="w-full px-2 py-1 border border-[#E5E5E0] focus:border-slate-700 focus:outline-none text-[12px] bg-white"
                      data-testid={`fa-3cd-block-select-${i}`}
                    >
                      <option value="">— Skip —</option>
                      {r.candidate_block_labels.map(bl => (
                        <option key={bl} value={bl}>
                          {bl}{bl === r.suggested_block_label ? " ★" : ""}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-5 py-3 border-t border-[#EDEDE7] bg-[#FAFAF7] flex items-center justify-between">
        <div className="text-[12px] text-[#52524E] font-mono">
          {readyCount} of {draft.length} ready · Total Opening WDV ₹ {inr(total)}
        </div>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="px-3.5 py-1.5 border border-slate-300 hover:bg-slate-100 text-[13px]"
          >
            Cancel
          </button>
          <button
            onClick={() => onApply(draft)}
            disabled={!readyCount}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-amber-700 hover:bg-amber-800 text-white text-[13px] disabled:opacity-50"
            data-testid="fa-3cd-apply-btn"
          >
            <Check size={14}/> Apply to Opening WDV
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

/* ==================== Roll-forward preview modal ==================== */
function RollForwardModal({ source, applying, onClose, onApply }) {
  const items = source.items || [];
  const total = items.reduce((s, r) => s + Number(r.closing_wdv || 0), 0);
  return (
    <ModalShell
      title={`Roll forward from FY ${source.src_fy}`}
      subtitle={source.src_name ? `Source run: ${source.src_name}` : `Source run: ${source.src_run_id}`}
      onClose={onClose}
    >
      <div className="px-5 py-3 text-[12.5px] text-[#52524E] bg-emerald-50 border-b border-emerald-200 flex items-start gap-2">
        <AlertCircle size={14} className="text-emerald-700 mt-0.5 shrink-0"/>
        <div>
          This copies the <b>Closing WDV</b> of each block from the previous year's computed run into this run's
          Opening WDV. Any existing opening values for the listed blocks will be <b>overwritten</b>.
        </div>
      </div>

      <div className="overflow-x-auto max-h-[55vh]">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-left bg-[#F9F9F8] text-[10.5px] font-mono uppercase tracking-wider text-slate-600">
              <th className="px-4 py-2">Block</th>
              <th className="px-3 py-2 text-right">Prior Closing WDV (₹)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#EDEDE7]">
            {items.length === 0 ? (
              <tr><td colSpan={2} className="px-4 py-4 text-center text-[12.5px] text-slate-500">
                No blocks with positive closing WDV in the prior run.
              </td></tr>
            ) : items.map((r, i) => (
              <tr key={r.block_label} data-testid={`fa-roll-row-${i}`}>
                <td className="px-4 py-1.5 font-medium">{r.block_label}</td>
                <td className="px-3 py-1.5 text-right font-mono">{inr(r.closing_wdv)}</td>
              </tr>
            ))}
            <tr className="bg-[#F2F2EE] font-semibold">
              <td className="px-4 py-2">TOTAL</td>
              <td className="px-3 py-2 text-right font-mono">{inr(total)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="px-5 py-3 border-t border-[#EDEDE7] bg-[#FAFAF7] flex items-center justify-end gap-2">
        <button
          onClick={onClose}
          className="px-3.5 py-1.5 border border-slate-300 hover:bg-slate-100 text-[13px]"
        >
          Cancel
        </button>
        <button
          onClick={onApply}
          disabled={applying || items.length === 0}
          className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white text-[13px] disabled:opacity-50"
          data-testid="fa-roll-apply-btn"
        >
          {applying ? <Loader2 size={14} className="animate-spin"/> : <RotateCw size={14}/>}
          Roll forward {items.length} block{items.length === 1 ? "" : "s"}
        </button>
      </div>
    </ModalShell>
  );
}

/* ==================== 3CD validation modal (read-only) ==================== */
const STATUS_META = {
  match:           { label: "Match",            cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  mismatch:        { label: "Mismatch",         cls: "bg-rose-50 text-rose-700 border-rose-200" },
  missing_in_3cd:  { label: "Not in 3CD",       cls: "bg-amber-50 text-amber-700 border-amber-200" },
  missing_in_excel:{ label: "Not in Opening",   cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

function Validate3CDModal({ report, onClose }) {
  const { ok, rows = [], totals, filename, tolerance } = report;
  const banner = ok
    ? { cls: "bg-emerald-50 border-emerald-200 text-emerald-800", text: `All rates tie back to 3CD (within ±₹${tolerance}).` }
    : { cls: "bg-rose-50 border-rose-200 text-rose-800",         text: "One or more rates do not tie back to 3CD. Review the rows below — sub-block opening WDV must sum to the 3CD rate-level closing WDV." };
  return (
    <ModalShell
      title="Validate Opening WDV against Prior 3CD"
      subtitle={filename}
      onClose={onClose}
      maxW="max-w-4xl"
    >
      <div className={`px-5 py-3 text-[12.5px] border-b ${banner.cls} flex items-start gap-2`}>
        {ok ? <Check size={14} className="mt-0.5 shrink-0"/> : <AlertCircle size={14} className="mt-0.5 shrink-0"/>}
        <div>{banner.text}</div>
      </div>

      <div className="overflow-x-auto max-h-[55vh]">
        <table className="w-full text-[12.5px]">
          <thead className="sticky top-0 bg-[#F9F9F8]">
            <tr className="text-left text-[10.5px] font-mono uppercase tracking-wider text-slate-600">
              <th className="px-4 py-2 text-center w-[80px]">Rate</th>
              <th className="px-3 py-2 text-right w-[160px]">Opening (Excel)</th>
              <th className="px-3 py-2 text-right w-[160px]">Closing WDV (3CD)</th>
              <th className="px-3 py-2 text-right w-[140px]">Diff</th>
              <th className="px-3 py-2 w-[120px]">Status</th>
              <th className="px-3 py-2">Sub-blocks contributing</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#EDEDE7]">
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                No comparable rate rows — both sources are empty.
              </td></tr>
            ) : rows.map(r => {
              const meta = STATUS_META[r.status] || STATUS_META.match;
              const driftCls = Math.abs(Number(r.diff || 0)) <= (tolerance || 1)
                ? "text-slate-600"
                : "text-rose-700 font-semibold";
              return (
                <tr key={r.rate} data-testid={`fa-validate-row-${r.rate}`}
                    className={r.status === "mismatch" ? "bg-rose-50/40" : ""}>
                  <td className="px-4 py-1.5 text-center font-mono font-semibold">{r.rate}%</td>
                  <td className="px-3 py-1.5 text-right font-mono">{inr(r.opening_excel)}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{inr(r.opening_3cd)}</td>
                  <td className={`px-3 py-1.5 text-right font-mono ${driftCls}`}>{inr(r.diff)}</td>
                  <td className="px-3 py-1.5">
                    <span className={`inline-block text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border ${meta.cls}`}>
                      {meta.label}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-[11.5px] text-slate-600">
                    {(r.blocks || []).length === 0
                      ? <span className="text-slate-400">—</span>
                      : (r.blocks || []).join(" · ")}
                  </td>
                </tr>
              );
            })}
            <tr className="bg-[#F2F2EE] font-semibold">
              <td className="px-4 py-2 text-center">TOTAL</td>
              <td className="px-3 py-2 text-right font-mono">{inr(totals?.opening_excel)}</td>
              <td className="px-3 py-2 text-right font-mono">{inr(totals?.opening_3cd)}</td>
              <td className="px-3 py-2 text-right font-mono">{inr(totals?.diff)}</td>
              <td colSpan={2}/>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="px-5 py-3 border-t border-[#EDEDE7] bg-[#FAFAF7] flex items-center justify-between">
        <div className="text-[11.5px] text-[#52524E]">
          Read-only check — nothing is written. Adjust the Excel and re-import to fix mismatches.
        </div>
        <button
          onClick={onClose}
          data-testid="fa-validate-close"
          className="px-3.5 py-1.5 border border-slate-300 hover:bg-slate-100 text-[13px]"
        >
          Close
        </button>
      </div>
    </ModalShell>
  );
}

/* ==================== Shared modal shell ==================== */
function ModalShell({ title, subtitle, children, onClose, maxW = "max-w-3xl" }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10">
      <div className={`bg-white shadow-xl w-full ${maxW} mx-4 border border-[#E5E5E0]`}>
        <div className="flex items-start justify-between px-5 py-3 border-b border-[#EDEDE7]">
          <div>
            <div className="font-heading text-base">{title}</div>
            {subtitle && <div className="text-[11.5px] text-slate-500 mt-0.5">{subtitle}</div>}
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="fa-modal-close">
            <X size={18}/>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
